import { MUIButton, MUISelect, MUITab, MUITextField } from "@abb/arcadia-ui-react"
import {
  ComponentType,
  MouseEvent,
  ReactElement,
  ReactNode,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
} from "react"
import {
  ControllerRenderProps,
  FieldErrors,
  FieldPath,
  FieldValues,
  UseFormHandleSubmit,
} from "react-hook-form"
import { Navigate } from "react-router-dom"
import { useHashTab } from "../../hooks/useHashTab"
import z from "zod"
import {
  ApplicationRoleApproverGet,
  ApplicationRoleApproverPut,
  ApplicationRoleApproverTypeEnum,
} from "../models/ApplicationRoleApprover"
import { DeviceGet } from "../models/Device"
import { FactoryTypeEnum } from "../models/Factory"
import { NotificationTargetType } from "../models/NotificationTarget"
import { TrainingPut } from "../models/Training"
import { TrainingCoursePut, TrainingCourseTypes } from "../models/TrainingCourse"
import { UserInfo } from "../models/User"
import { EntityRow } from "../pages/partials/EntityTab"
import { booleanComparator, getDoneIcon, getEmptyIcon } from "../utils/getBooleanFlagIcon"
import { dateStringComparator, getNiceFormattedDate } from "../utils/getFormattedDate"
import DiMaCheckboxList from "./DiMaCheckboxList"
import { DiMaDetailsHeader, DiMaDetailsHeaderProps } from "./DiMaDetailsHeader"
import { DiMaProgress } from "./DiMaProgress"

type PriorityValue = number | string | undefined

export function renderApplicationRoleApproverContainer({
  hasRequiredParams,
  returnURL,
  approver,
  isLoadingUser,
  user,
  activeTab,
  tabItems,
  handleTabChange,
  detailsContent,
  additionalContent,
}: Readonly<{
  hasRequiredParams: boolean
  returnURL: string
  approver?: ApplicationRoleApproverGet
  isLoadingUser: boolean
  user?: UserInfo
  activeTab: string
  tabItems: Array<{ id: string; label: string }>
  handleTabChange: (id: string) => void
  detailsContent: ReactNode
  additionalContent?: ReactNode
}>) {
  if (!hasRequiredParams) {
    return <Navigate to={returnURL} />
  }

  if (!approver || isLoadingUser || !user) {
    return <DiMaProgress />
  }

  const detailsHeaderProps: DiMaDetailsHeaderProps = {
    url: returnURL,
    title: `${approver.user.first} ${approver.user.last}`,
    label: "Role",
    subtitle: approver.role.name,
    hideCopyToClipboard: true,
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <DiMaDetailsHeader {...detailsHeaderProps} />
      <MUITab tabItems={tabItems} value={activeTab} handleChange={handleTabChange} />
      {detailsContent}
      {additionalContent}
    </div>
  )
}

export function createNotificationValidationSchema({
  includeApplicationId = false,
  includeTargetRoles = false,
}: Readonly<{
  includeApplicationId?: boolean
  includeTargetRoles?: boolean
}> = {}) {
  const baseFields = {
    topicId: z.string().min(1, "Topic is required"),
    title: z.string().min(1, "Title is required"),
    body: z.string().min(1, "Body is required"),
    url: z
      .string()
      .nullable()
      .refine(
        (val) =>
          !val ||
          /^https?:\/\/(?:[\w-]{1,63}\.)*abb\.com(?:\/[^\s?#]*)?(?:\?[^\s#]*)?$/.test(
            val
          ),
        {
          message: "URL must be well-formed and end with 'abb.com'",
        }
      ),
    applicationVersion: z.string().nullable(),
    operatingSystem: z.string().nullable(),
    validityStartDate: z
      .string()
      .min(1, "Validity Start Date is required")
      .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
    validityEndDate: z
      .string()
      .min(1, "Validity End Date is required")
      .refine((val) => !Number.isNaN(Date.parse(val)), "Invalid date format"),
    isAnonymous: z.boolean(),
    targetType: z.nativeEnum(NotificationTargetType, {
      message: "Notification Target is required",
    }),
  }

  const schemaShape = {
    ...(includeApplicationId
      ? { applicationId: z.string().min(1, "Application is required") }
      : {}),
    ...baseFields,
    ...(includeTargetRoles
      ? {
          targetRoles: z.array(z.string(), {
            error: "At least one target role is required",
          }),
        }
      : {}),
  }

  return z.object(schemaShape)
}

export function createDeviceSoftwarePackageFirmwareConstraintValidationSchema() {
  const maxCharacters = 15
  const swVersionRegex = /^\d{2}\.\d{2}\.\d{2}$/

  return z.object({
    softwarePackageEditionCode: z
      .string()
      .min(1, { message: "Software Package Edition Code is required" })
      .max(maxCharacters, {
        message: "Software Package Edition Code must be at most 15 characters long",
      }),
    mainboardMinSoftwareVersion: z
      .string()
      .min(1, { message: "Mainboard Min SW Version is required" })
      .regex(swVersionRegex, {
        message: "Mainboard Min SW Version must be in format XX.XX.XX (e.g. 01.02.03)",
      }),
    hmiMinSoftwareVersion: z
      .string()
      .min(1, { message: "HMI Min SW Version is required" })
      .regex(swVersionRegex, {
        message: "HMI Min SW Version must be in format XX.XX.XX (e.g. 01.02.03)",
      }),
  })
}

export function createApplicationPermissionFirmwareConstraintValidationSchema() {
  const swVersionRegex = /^\d{2}\.\d{2}\.\d{2}$/

  return z.object({
    mainboardMinSoftwareVersion: z
      .string()
      .min(1, { message: "Mainboard Min SW Version is required" })
      .regex(swVersionRegex, {
        message: "Mainboard Min SW Version must be in format XX.XX.XX (e.g. 01.02.03)",
      }),
    hmiMinSoftwareVersion: z
      .string()
      .optional()
      .refine((v) => !v || swVersionRegex.test(v), {
        message: "HMI Min SW Version must be in format XX.XX.XX (e.g. 01.02.03)",
      }),
  })
}

export function DetailsHeaderTabLayout({
  detailsHeaderProps,
  tabItems,
  activeTab,
  onTabChange,
}: Readonly<{
  detailsHeaderProps: DiMaDetailsHeaderProps
  tabItems: Array<{ id: string; label: string }>
  activeTab: string
  onTabChange: (id: string) => void
}>) {
  return (
    <>
      <DiMaDetailsHeader {...detailsHeaderProps} />

      <MUITab tabItems={tabItems} value={activeTab} handleChange={onTabChange} />
    </>
  )
}

export function createUserApplicationsColumns(
  userId: string | undefined,
  renderStatus: (row: EntityRow) => ReactNode
) {
  return [
    {
      field: "name",
      headerName: "Name",
      isLink: true,
      linkBasePath: `/users/${userId}/applications`,
    },
    { field: "applicationShortName", headerName: "App" },
    {
      field: "validityStartDate",
      headerName: "Validity Start Date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.validityStartDate)),
      sortComparator: dateStringComparator,
    },
    {
      field: "validityEndDate",
      headerName: "Validity End Date",
      renderCell: (row: EntityRow) => getNiceFormattedDate(String(row.validityEndDate)),
      sortComparator: dateStringComparator,
    },
    {
      field: "status",
      headerName: "Status",
      renderCell: renderStatus,
    },
    {
      field: "hasProperties",
      headerName: "Has Properties",
      renderCell: (row: EntityRow) =>
        row.hasProperties ? getDoneIcon() : getEmptyIcon(),
      sortComparator: booleanComparator,
    },
  ]
}

export function DetailsHeaderTab({
  detailsHeaderProps,
  tabItems,
  activeTab,
  onTabChange,
}: Readonly<{
  detailsHeaderProps: DiMaDetailsHeaderProps
  tabItems: Array<{ id: string; label: string }>
  activeTab: string
  onTabChange: (id: string) => void
}>) {
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1 }}>
        <div style={{ gridColumn: "span 10" }}>
          <DiMaDetailsHeader {...detailsHeaderProps} />
        </div>
      </div>
      <MUITab tabItems={tabItems} value={activeTab} handleChange={onTabChange} />
    </>
  )
}

export function renderDetailsTabSection<DetailsProps extends Record<string, unknown>>({
  detailsHeaderProps,
  activeTab,
  handleTabChange,
  DetailsComponent,
  detailsComponentProps,
}: Readonly<{
  detailsHeaderProps: DiMaDetailsHeaderProps
  activeTab: string
  handleTabChange: (id: string) => void
  DetailsComponent: ComponentType<DetailsProps>
  detailsComponentProps: DetailsProps
}>) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <div style={{ display: "flex", flexDirection: "row" }}>
        <div style={{ flex: 10 }}>
          <DiMaDetailsHeader {...detailsHeaderProps} />
        </div>
      </div>
      <MUITab
        tabItems={[{ id: "details", label: "Details" }]}
        value={activeTab}
        handleChange={handleTabChange}
      />
      {activeTab === "details" && <DetailsComponent {...detailsComponentProps} />}
    </div>
  )
}

export function renderDetailsTabWithGrid<DetailsProps extends Record<string, unknown>>({
  detailsHeaderProps,
  activeTab,
  handleTabChange,
  DetailsComponent,
  detailsComponentProps,
}: Readonly<{
  detailsHeaderProps: DiMaDetailsHeaderProps
  activeTab: string
  handleTabChange: (id: string) => void
  DetailsComponent: ComponentType<DetailsProps>
  detailsComponentProps: DetailsProps
}>) {
  return (
    <div style={{ display: "flex", flexDirection: "column", flexGrow: 1 }}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 1 }}>
        <div style={{ gridColumn: "span 10" }}>
          <DiMaDetailsHeader {...detailsHeaderProps} />
        </div>
      </div>
      <MUITab
        tabItems={[{ id: "details", label: "Details" }]}
        value={activeTab}
        handleChange={handleTabChange}
      />
      {activeTab === "details" && <DetailsComponent {...detailsComponentProps} />}
    </div>
  )
}

export function HashTabSection({
  defaultTab,
  tabItems,
  tabContent,
}: Readonly<{
  defaultTab: string
  tabItems: Array<{ id: string; label: string }>
  tabContent: Record<string, ReactNode>
}>) {
  const { activeTab, handleTabChange } = useHashTab(defaultTab)

  const activeContent = tabContent[activeTab] ?? null

  return (
    <div>
      <MUITab tabItems={tabItems} value={activeTab} handleChange={handleTabChange} />
      {activeContent}
    </div>
  )
}

export function useApplicationSelectRenderer<
  FormValues extends FieldValues & { applicationId: string },
>({
  applicationsList,
  errors,
  handleApplicationSelectChange,
  isLoadingApplications,
}: Readonly<{
  applicationsList: ReadonlyArray<{ id: number | string; name: string }>
  errors: FieldErrors<FormValues>
  handleApplicationSelectChange: (
    field: ControllerRenderProps<FormValues, "applicationId" & FieldPath<FormValues>>
  ) => (values: Array<string | { id: string }>) => void
  isLoadingApplications: boolean
}>) {
  return useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "applicationId" & FieldPath<FormValues>>
    }) => (
      <MUISelect
        name={field.name}
        id="applicationId"
        size={32 as const}
        dropdownLOV={applicationsList.map((a) => ({
          id: String(a.id),
          name: a.name ?? "",
        }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleApplicationSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={isLoadingApplications}
        errors={
          errors.applicationId?.message
            ? { applicationId: { message: String(errors.applicationId.message) } }
            : undefined
        }
      />
    ),
    [
      applicationsList,
      errors.applicationId,
      handleApplicationSelectChange,
      isLoadingApplications,
    ]
  )
}

export function useApproverTypeSelectRenderer({
  errors,
  valueExtractor,
}: Readonly<{
  errors?: FieldErrors<ApplicationRoleApproverPut>
  valueExtractor: (first: unknown) => string
}>) {
  const valueExtractorRef = useRef(valueExtractor)

  useEffect(() => {
    valueExtractorRef.current = valueExtractor
  }, [valueExtractor])

  const handleTypeChange = useCallback(
    (field: ControllerRenderProps<ApplicationRoleApproverPut, "type">) =>
      (values: unknown) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = valueExtractorRef.current(first)
        field.onChange(value)
      },
    []
  )

  return useCallback(
    ({ field }: { field: ControllerRenderProps<ApplicationRoleApproverPut, "type"> }) => {
      const selectProps = {
        name: field.name,
        id: "type",
        size: 32 as const,
        dropdownLOV: Object.values(ApplicationRoleApproverTypeEnum).map((v) => ({
          id: String(v),
          name: String(v),
        })),
        selectedItem: field.value ? [String(field.value)] : [],
        onChange: handleTypeChange(field),
        onBlur: field.onBlur,
      }

      const selectErrors = errors?.type?.message
        ? { type: { message: String(errors.type.message) } }
        : undefined

      if (selectErrors) {
        return <MUISelect {...selectProps} errors={selectErrors} />
      }

      return <MUISelect {...selectProps} />
    },
    [errors?.type, handleTypeChange]
  )
}

export function usePriorityTextFieldRenderer<
  FormValues extends FieldValues & { priority: PriorityValue },
>({
  errors,
  handleParsedNumberChange,
}: Readonly<{
  errors: FieldErrors<FormValues>
  handleParsedNumberChange: (
    field: ControllerRenderProps<FormValues, "priority" & FieldPath<FormValues>>
  ) => (value: string) => void
}>) {
  return useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "priority" & FieldPath<FormValues>>
    }) => (
      <MUITextField
        id="priority"
        name={field.name}
        type="number"
        size={32 as const}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.priority?.message === "string"
            ? { priority: { message: errors.priority.message } }
            : undefined
        }
      />
    ),
    [errors, handleParsedNumberChange]
  )
}

export function useApplicationRolePriorityFieldRenderer<
  FormValues extends FieldValues & { priority: PriorityValue },
>({
  errors,
  handleParsedNumberChange,
}: Readonly<{
  errors: FieldErrors<FormValues>
  handleParsedNumberChange: (
    field: ControllerRenderProps<FormValues, "priority" & FieldPath<FormValues>>
  ) => (value: string) => void
}>) {
  return useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "priority" & FieldPath<FormValues>>
    }) => (
      <MUITextField
        id="priority"
        name="priority"
        type="number"
        size={32 as const}
        value={
          field.value !== undefined && field.value !== null ? String(field.value) : ""
        }
        onChange={handleParsedNumberChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.priority?.message === "string"
            ? { priority: { message: errors.priority.message } }
            : undefined
        }
      />
    ),
    [handleParsedNumberChange, errors.priority]
  )
}

export function useFeedbackTopicPriorityField<
  FormValues extends FieldValues & { priority: PriorityValue },
>({ errors }: Readonly<{ errors: FieldErrors<FormValues> }>) {
  const handleParsedNumberChange = useCallback(
    (field: ControllerRenderProps<FormValues, "priority" & FieldPath<FormValues>>) =>
      (value: string) => {
        const parsed = value ? Number.parseInt(value, 10) : ""
        field.onChange(parsed)
      },
    []
  )

  return usePriorityTextFieldRenderer({
    errors,
    handleParsedNumberChange,
  })
}

export function useIssueReportTopicPriorityField<
  FormValues extends FieldValues & { priority: PriorityValue },
>({ errors }: Readonly<{ errors: FieldErrors<FormValues> }>) {
  return useFeedbackTopicPriorityField({ errors })
}

export function useApplicationKeyValuePairTextFieldRenderers<
  FormValues extends FieldValues &
    Record<"key", string | undefined> &
    Record<"value", string | undefined>,
>({
  errors,
  handleKeyChange,
  handleValueChange,
}: Readonly<{
  errors: FieldErrors<FormValues>
  handleKeyChange: (
    field: ControllerRenderProps<FormValues, "key" & FieldPath<FormValues>>
  ) => (value: string) => void
  handleValueChange: (
    field: ControllerRenderProps<FormValues, "value" & FieldPath<FormValues>>
  ) => (value: string) => void
}>) {
  const renderTextFieldKey = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "key" & FieldPath<FormValues>>
    }) => (
      <MUITextField
        id="key"
        type="text"
        size={32 as const}
        name={field.name}
        value={field.value}
        onChange={handleKeyChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.key?.message === "string"
            ? { key: { message: errors.key.message } }
            : undefined
        }
      />
    ),
    [errors.key, handleKeyChange]
  )

  const renderTextFieldValue = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "value" & FieldPath<FormValues>>
    }) => (
      <MUITextField
        id="value"
        type="text"
        size={32 as const}
        name={field.name}
        value={field.value}
        onChange={handleValueChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.value?.message === "string"
            ? { value: { message: errors.value.message } }
            : undefined
        }
      />
    ),
    [errors.value, handleValueChange]
  )

  return { renderTextFieldKey, renderTextFieldValue }
}

export function useFactoryCheckboxHelper<
  FormValues extends FieldValues & { isFree?: boolean; isEnabled?: boolean },
>({
  renderCheckbox,
}: Readonly<{
  renderCheckbox: (
    field: ControllerRenderProps<FormValues, "isFree" & FieldPath<FormValues>>,
    handleToggle: (event: MouseEvent<HTMLElement>) => void
  ) => ReactElement
}>) {
  const handleBooleanToggle = useCallback(
    (
      field: ControllerRenderProps<
        FormValues,
        ("isFree" | "isEnabled") & FieldPath<FormValues>
      >
    ) =>
      (e: MouseEvent<HTMLElement>) => {
        e.stopPropagation()
        field.onChange(!field.value)
      },
    []
  )

  const renderCheckboxIsFree = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<FormValues, "isFree" & FieldPath<FormValues>>
    }) => renderCheckbox(field, handleBooleanToggle(field)),
    [handleBooleanToggle, renderCheckbox]
  )

  return { handleBooleanToggle, renderCheckboxIsFree }
}

export function createFactoryValidationSchemas() {
  const FactoryTypeSchema = z.enum(
    Object.keys(FactoryTypeEnum) as [keyof typeof FactoryTypeEnum]
  )

  const validationSchema = z.object({
    name: z.string().min(1, "Name is required"),
    type: FactoryTypeSchema.refine((key) => key in FactoryTypeEnum, {
      message: "Invalid Type",
    }),
    isFree: z.boolean({ error: "IsFree is required" }),
    isEnabled: z.boolean({ error: "IsEnabled is required" }),
    maxDays: z.number().int().min(1, "MaxDays is required"),
  })

  return { FactoryTypeSchema, validationSchema }
}

type TrainingTextFieldName =
  | "name"
  | "title"
  | "description"
  | "imageUrl"
  | "deviceImageUrl"
  | "summary"
  | "disclaimer"
  | "completionMessage"

type TrainingNumberFieldName = "validityMonths" | "expiryReminderDays"

const MAX_TEXT_LENGTH = 255
const MAX_TRAINING_NUMBER_VALUE = 255
const MAX_COURSE_NUMBER_VALUE = 255

const requiredText = (label: string) =>
  z
    .string()
    .min(1, `${label} is required`)
    .max(MAX_TEXT_LENGTH, `${label} must be at most ${MAX_TEXT_LENGTH} characters`)

export function createTrainingValidationSchema(
  existingNames: string[] = [],
  { includeApplicationId = false }: Readonly<{ includeApplicationId?: boolean }> = {}
) {
  const takenNames = new Set(existingNames.map((name) => name.trim().toLowerCase()))

  return z.object({
    ...(includeApplicationId
      ? { applicationId: z.string().min(1, "Application is required") }
      : {}),
    name: requiredText("Name").refine(
      (name) => !takenNames.has(name.trim().toLowerCase()),
      "A training with this name already exists"
    ),
    title: requiredText("Title"),
    description: requiredText("Description"),
    imageUrl: requiredText("Image URL"),
    deviceImageUrl: requiredText("Device image URL"),
    summary: requiredText("Summary"),
    disclaimer: requiredText("Disclaimer"),
    completionMessage: requiredText("Completion message"),
    validityMonths: z
      .number({ error: "Validity months is required" })
      .int("Validity months must be an integer")
      .min(1, `Validity months must be between 1 and ${MAX_TRAINING_NUMBER_VALUE}`)
      .max(
        MAX_TRAINING_NUMBER_VALUE,
        `Validity months must be between 1 and ${MAX_TRAINING_NUMBER_VALUE}`
      ),
    expiryReminderDays: z
      .number({ error: "Expiry reminder days must be a number" })
      .int("Expiry reminder days must be an integer")
      .min(0, `Expiry reminder days must be between 0 and ${MAX_TRAINING_NUMBER_VALUE}`)
      .max(
        MAX_TRAINING_NUMBER_VALUE,
        `Expiry reminder days must be between 0 and ${MAX_TRAINING_NUMBER_VALUE}`
      )
      .nullable(),
  })
}

export function useTrainingFieldRenderers<T extends TrainingPut = TrainingPut>({
  errors,
}: Readonly<{ errors: FieldErrors<T> }>) {
  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<T, TrainingTextFieldName & FieldPath<T>>) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleNumberFieldChange = useCallback(
    (field: ControllerRenderProps<T, TrainingNumberFieldName & FieldPath<T>>) =>
      (value: string) => {
        if (value) {
          field.onChange(Number.parseInt(value, 10))
          return
        }
        field.onChange(field.name === "expiryReminderDays" ? null : "")
      },
    []
  )

  const renderTextField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<T, TrainingTextFieldName & FieldPath<T>>
    }) => {
      const message = errors[field.name]?.message
      return (
        <MUITextField
          id={field.name}
          type="text"
          size={32 as const}
          name={field.name}
          value={field.value ?? ""}
          onChange={handleTextFieldChange(field)}
          onBlur={field.onBlur}
          isDisabled={field.disabled}
          errors={typeof message === "string" ? { [field.name]: { message } } : undefined}
        />
      )
    },
    [errors, handleTextFieldChange]
  )

  const renderNumberField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<T, TrainingNumberFieldName & FieldPath<T>>
    }) => {
      const message = errors[field.name]?.message
      return (
        <MUITextField
          id={field.name}
          type="number"
          size={32 as const}
          name={field.name}
          value={
            field.value !== undefined && field.value !== null ? String(field.value) : ""
          }
          onChange={handleNumberFieldChange(field)}
          onBlur={field.onBlur}
          isDisabled={field.disabled}
          errors={typeof message === "string" ? { [field.name]: { message } } : undefined}
        />
      )
    },
    [errors, handleNumberFieldChange]
  )

  return { renderTextField, renderNumberField }
}

type TrainingCourseTextFieldName =
  "name" | "title" | "description" | "contentUrl" | "duration" | "disclaimer"

type TrainingCourseNumberFieldName = "position"

export function createTrainingCourseValidationSchema(
  existingCourses: Array<{ name: string; position: number }> = []
) {
  const takenNames = new Set(
    existingCourses.map((course) => course.name.trim().toLowerCase())
  )
  const takenPositions = new Set(existingCourses.map((course) => course.position))

  return z.object({
    name: requiredText("Name").refine(
      (name) => !takenNames.has(name.trim().toLowerCase()),
      "A course with this name already exists in this training"
    ),
    title: requiredText("Title"),
    description: requiredText("Description"),
    type: z
      .string()
      .refine(
        (type) => (TrainingCourseTypes as readonly string[]).includes(type),
        "Type must be Video or Document"
      ),
    contentUrl: requiredText("Content URL"),
    duration: requiredText("Duration"),
    disclaimer: z
      .string()
      .max(MAX_TEXT_LENGTH, `Disclaimer must be at most ${MAX_TEXT_LENGTH} characters`)
      .optional(),
    position: z
      .number({ error: "Position is required" })
      .int("Position must be an integer")
      .min(1, `Position must be between 1 and ${MAX_COURSE_NUMBER_VALUE}`)
      .max(
        MAX_COURSE_NUMBER_VALUE,
        `Position must be between 1 and ${MAX_COURSE_NUMBER_VALUE}`
      )
      .refine(
        (position) => !takenPositions.has(position),
        "A course with this position already exists in this training"
      ),
  })
}

export function useTrainingCourseFieldRenderers({
  errors,
}: Readonly<{ errors: FieldErrors<TrainingCoursePut> }>) {
  const handleTextFieldChange = useCallback(
    (field: ControllerRenderProps<TrainingCoursePut, TrainingCourseTextFieldName>) =>
      (value: string) =>
        field.onChange(value),
    []
  )

  const handleNumberFieldChange = useCallback(
    (field: ControllerRenderProps<TrainingCoursePut, TrainingCourseNumberFieldName>) =>
      (value: string) =>
        field.onChange(value ? Number.parseInt(value, 10) : ""),
    []
  )

  const handleTypeSelectChange = useCallback(
    (field: ControllerRenderProps<TrainingCoursePut, "type">) =>
      (values: Array<string | { id: string }>) => {
        const first = Array.isArray(values) && values.length > 0 ? values[0] : ""
        const value = typeof first === "string" ? first : (first?.id ?? "")
        field.onChange(value)
      },
    []
  )

  const renderTextField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<TrainingCoursePut, TrainingCourseTextFieldName>
    }) => {
      const message = errors[field.name]?.message
      return (
        <MUITextField
          id={field.name}
          type="text"
          size={32 as const}
          name={field.name}
          value={field.value ?? ""}
          onChange={handleTextFieldChange(field)}
          onBlur={field.onBlur}
          isDisabled={field.disabled}
          errors={typeof message === "string" ? { [field.name]: { message } } : undefined}
        />
      )
    },
    [errors, handleTextFieldChange]
  )

  const renderNumberField = useCallback(
    ({
      field,
    }: {
      field: ControllerRenderProps<TrainingCoursePut, TrainingCourseNumberFieldName>
    }) => {
      const message = errors[field.name]?.message
      return (
        <MUITextField
          id={field.name}
          type="number"
          size={32 as const}
          name={field.name}
          value={
            field.value !== undefined && field.value !== null ? String(field.value) : ""
          }
          onChange={handleNumberFieldChange(field)}
          onBlur={field.onBlur}
          isDisabled={field.disabled}
          errors={typeof message === "string" ? { [field.name]: { message } } : undefined}
        />
      )
    },
    [errors, handleNumberFieldChange]
  )

  const renderTypeSelect = useCallback(
    ({ field }: { field: ControllerRenderProps<TrainingCoursePut, "type"> }) => (
      <MUISelect
        name={field.name}
        id="type"
        size={32}
        dropdownLOV={TrainingCourseTypes.map((type) => ({ id: type, name: type }))}
        selectedItem={field.value ? [String(field.value)] : []}
        onChange={handleTypeSelectChange(field)}
        onBlur={field.onBlur}
        isDisabled={field.disabled}
        errors={
          typeof errors.type?.message === "string"
            ? { type: { message: errors.type.message } }
            : undefined
        }
      />
    ),
    [errors.type, handleTypeSelectChange]
  )

  return { renderTextField, renderNumberField, renderTypeSelect }
}

export function renderDevicesSelectionLayout<FormValues extends FieldValues>({
  devices,
  checkedItemsIds,
  setCheckedItemsIds,
  onClickSelectAll,
  onClickUnselectAll,
  handleSubmit,
  onSubmit,
  isDirty,
  isSubmitting,
}: Readonly<{
  devices: DeviceGet[] | null
  checkedItemsIds: string[] | undefined
  setCheckedItemsIds: (ids: SetStateAction<string[]>) => void
  onClickSelectAll: () => void
  onClickUnselectAll: () => void
  handleSubmit: UseFormHandleSubmit<FormValues>
  onSubmit: (data: FormValues) => Promise<void> | void
  isDirty: boolean
  isSubmitting: boolean
}>): ReactNode {
  if (!devices) {
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "50vh",
        }}
      >
        <DiMaProgress />
      </div>
    )
  }

  return (
    <div className="content-container">
      <div className="right-content">
        <div
          className="table"
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
            width: "100%",
            paddingTop: 16,
          }}
        >
          <div
            className="table-header"
            style={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <MUIButton
                onClickButton={onClickSelectAll}
                label="Select All"
                variant="primary"
                size={32 as const}
              />
              <MUIButton
                onClickButton={onClickUnselectAll}
                label="Unselect All"
                variant="secondary"
                size={32 as const}
              />
              <MUIButton
                variant="primary"
                onClickButton={handleSubmit(onSubmit)}
                disabled={isSubmitting || !isDirty}
                isLoading={isSubmitting}
                label="Save"
                size={32 as const}
              />
            </div>
          </div>

          <div
            style={{
              maxHeight: "60vh",
              overflow: "auto",
              borderRadius: 10,
              overflowX: "hidden",
            }}
          >
            <DiMaCheckboxList
              items={devices.map((device) => ({
                id: device.id,
                title: `${device.slaveId} - ${device.name}`,
              }))}
              checkedItemsIds={checkedItemsIds ?? []}
              setCheckedItemsIds={setCheckedItemsIds}
              options={{ sortItemsByTitle: true, sortCheckedItemsIds: true }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
